import { forwardRef, Ref, useState } from 'react'
import * as Select from '@radix-ui/react-select'
import { ChevronDownIcon, XIcon } from 'lucide-react'

import { cn } from '@/lib/utils'

interface CustomSelectProps {
  options: { value: string; text: string }[]
  placeholder?: string
  value?: string
  // eslint-disable-next-line no-unused-vars
  onChange?: (value: string) => void
  onReset?: () => void
  error?: boolean
  disabled?: boolean
  resetKey?: string | number
  variant?: 'default' | 'newsletter' | 'outlined' | 'light'
}

const CustomSelect = forwardRef(
  (
    {
      options,
      placeholder,
      value,
      onChange,
      onReset,
      error,
      disabled,
      resetKey,
      variant = 'default'
    }: CustomSelectProps,
    ref: Ref<HTMLButtonElement>
  ) => {
    const [internalResetKey, setInternalResetKey] = useState(0)
    const safeOptions = options?.filter((option) => option.value !== '')

    const handleValueChange = (newValue: string) => {
      onChange?.(newValue)
    }

    return (
      <Select.Root
        key={`${resetKey}-${internalResetKey}`}
        value={value}
        onValueChange={handleValueChange}
        disabled={disabled}
      >
        <Select.Trigger
          ref={ref}
          className={cn(
            'flex w-full appearance-none justify-between bg-transparent outline-none',
            'font-helvetica leading-[120%] tracking-[0.036rem]',
            {
              'border-dark/10 placeholder:text-dark dark:text-white dark:placeholder:text-white rounded-radius-minimal border-b px-[1.5rem] py-[1.65rem] text-[1.6rem] dark:border-[white]/20':
                variant === 'default',
              'border-dark/10 placeholder:text-dark dark:placeholder:text-white h-[4.8rem] items-center rounded-[0.5rem] border bg-border-primary-light px-[1.6rem] text-[1.6rem] dark:border-[white]/20 dark:text-text-primary-dark':
                variant === 'outlined',
              'border !border-border-primary-light/30 px-[1.5rem] py-[1.65rem] text-[1.6rem] text-text-primary-light':
                variant === 'light'
            },
            'focus:border-dark/40 focus:ring-dark/20 focus:ring-2 dark:focus:border-[white]/40 dark:focus:ring-[white]/20',
            {
              'border-red-500 focus:border-red-500 focus:ring-red-500/50':
                error,
              'dark:border-red-500 dark:focus:border-red-500 dark:focus:ring-red-500/50':
                error,
              'pointer-events-none opacity-40': disabled
            }
          )}
        >
          <Select.Value placeholder={placeholder} />
          <div className="flex items-center space-x-2">
            {value && onReset && (
              <span
                role="button"
                tabIndex={0}
                aria-label="Rimuovi filtro"
                onPointerDown={(e) => {
                  e.stopPropagation()
                  e.preventDefault()
                  setInternalResetKey((k) => k + 1)
                  onReset()
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    e.stopPropagation()
                    setInternalResetKey((k) => k + 1)
                    onReset()
                  }
                }}
                className="text-dark-dark/50 hover:text-dark-dark cursor-pointer"
              >
                <XIcon size={13} aria-hidden="true" />
              </span>
            )}
            <Select.Icon>
              <ChevronDownIcon size={15} />
            </Select.Icon>
          </div>
        </Select.Trigger>

        <Select.Portal>
          <Select.Content
            position="popper"
            className={cn(
              'z-10 max-h-[300px] w-full',
              {
                'border-dark/10 mt-4 rounded-radius-minimal border border-[white]/20 bg-[white] dark:bg-black-light':
                  variant === 'default',
                'rounded-radius-minimal border border-[white]/20 bg-border-primary-light text-text-primary-dark':
                  variant === 'outlined',
                'mt-[4px] rounded-radius-minimal border border-border-secondary-light bg-[white] text-text-primary-light':
                  variant === 'light'
              },
              'w-[var(--radix-select-trigger-width)]'
            )}
            data-lenis-prevent="true"
          >
            <Select.Viewport className="p-1">
              {safeOptions?.map((option) => (
                <Select.Item
                  key={option.value}
                  value={option.value}
                  className={cn(
                    'cursor-pointer font-helvetica leading-[120%] tracking-[0.036rem]',
                    {
                      'rounded-radius-minimal px-[1.5rem] py-[1.2rem] text-[1.6rem] leading-[120%] tracking-[0.036rem] outline-black-light/20':
                        variant === 'default',
                      'p-[1.6rem] text-[1.6rem] outline-black-light/20':
                        variant === 'outlined',
                      'px-[1.5rem] py-[1.2rem] text-[1.6rem] text-text-primary-light outline-none':
                        variant === 'light'
                    },
                    'data-[highlighted]:bg-[black]/5 data-[state=checked]:bg-[black]/10'
                  )}
                >
                  <Select.ItemText>{option.text}</Select.ItemText>
                </Select.Item>
              ))}
            </Select.Viewport>
          </Select.Content>
        </Select.Portal>
      </Select.Root>
    )
  }
)

CustomSelect.displayName = 'CustomSelect'

export default CustomSelect
